/**
 * Properties Inspector Component
 * Main component for inspecting element properties
 */

import React, { useEffect, useState } from 'react';
import { useViewerStore } from '@/store/viewerStore';
import { usePropertiesStore } from '@/store/propertiesStore';
import { PropertiesService } from '@/services/core/propertiesTools';
import { ViewerService } from '@/services/core/viewerTools';
import { PropertyList } from './PropertyList';
import { PropertySearch } from './PropertySearch';
import { GeometryToolsTester } from './GeometryToolsTester';
import { toast } from '@/components/common/Toast';

export function PropertiesInspector() {
  const { viewer, isModelLoaded } = useViewerStore();
  const {
    selectedDbId,
    selectedElementName,
    properties,
    isLoading,
    showHiddenProperties,
    setSelectedElement,
    setProperties,
    setIsLoading,
    toggleShowHidden,
    clearSelection
  } = usePropertiesStore();

  const [error, setError] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [inputDbId, setInputDbId] = useState<string>('');
  const [idType, setIdType] = useState<'dbId' | 'revitId'>('dbId');
  const [showGeometryTester, setShowGeometryTester] = useState(false);

  // Set up viewer selection event
  useEffect(() => {
    if (!viewer || !isModelLoaded) return;

    const handleSelection = (event: any) => {
      if (event.dbIdArray && event.dbIdArray.length > 0) {
        const dbId = event.dbIdArray[0]; // Take first selected element
        loadElementProperties(dbId);
      }
    };

    // Only add listener if in selection mode
    if (isSelectionMode) {
      viewer.addEventListener(
        Autodesk.Viewing.SELECTION_CHANGED_EVENT,
        handleSelection
      );
    }

    return () => {
      if (isSelectionMode) {
        viewer.removeEventListener(
          Autodesk.Viewing.SELECTION_CHANGED_EVENT,
          handleSelection
        );
      }
    };
  }, [viewer, isModelLoaded, isSelectionMode]);

  const loadElementProperties = async (dbId: number) => {
    if (!viewer || !viewer.model) {
      setError('Viewer or model not available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // First check if the dbId exists in the model
      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        setError('Model tree not available');
        setIsLoading(false);
        return;
      }

      // Validate that the dbId exists
      let validDbId = false;
      tree.enumNodeChildren(tree.getRootId(), (childId) => {
        if (childId === dbId) {
          validDbId = true;
          return false; // Stop enumeration
        }
      }, true);

      if (!validDbId) {
        setError(`Element with ID ${dbId} not found in the model`);
        setIsLoading(false);
        return;
      }

      // Get element name from tree
      const elementName = tree.getNodeName(dbId) || `Element ${dbId}`;
      
      setSelectedElement(dbId, elementName);

      // Load properties
      const props = await PropertiesService.getElementProperties(viewer, dbId);
      
      if (props.length === 0) {
        setError(`No properties found for element ${dbId}`);
      } else {
        setProperties(props);
        // Highlight element
        ViewerService.highlightElement(viewer, dbId);
        // Clear the input after successful load
        setInputDbId('');
      }
    } catch (err) {
      setError(`Failed to load properties for element ${dbId}: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const loadElementByRevitId = async (revitId: string) => {
    if (!viewer || !viewer.model) {
      setError('Viewer or model not available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Find the DbID for this Revit ID
      const dbId = await PropertiesService.findElementByRevitId(viewer, revitId);
      
      if (dbId === null) {
        setError(`No element found with Revit ID: ${revitId}`);
        setIsLoading(false);
        return;
      }

      // Load properties using the found DbID
      await loadElementProperties(dbId);
    } catch (err) {
      setError(`Failed to find element with Revit ID ${revitId}: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const handleLoadElement = async () => {
    if (idType === 'dbId') {
      const dbId = parseInt(inputDbId);
      if (!isNaN(dbId) && dbId > 0) {
        await loadElementProperties(dbId);
      } else {
        setError('Please enter a valid DbID (numeric value)');
      }
    } else {
      // Revit ID mode
      if (inputDbId.trim()) {
        await loadElementByRevitId(inputDbId.trim());
      } else {
        setError('Please enter a valid Revit Element ID');
      }
    }
  };

  const handleToggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (!isSelectionMode) {
      // Entering selection mode
      setError(null);
    } else {
      // Exiting selection mode
      if (viewer) {
        ViewerService.clearHighlights(viewer);
      }
    }
  };

  const handleClearSelection = () => {
    clearSelection();
    if (viewer) {
      ViewerService.clearHighlights(viewer);
    }
    setError(null);
  };

  const handleExportCSV = () => {
    if (!selectedElementName || properties.length === 0) return;
    PropertiesService.exportToCSV(selectedElementName, properties);
  };

  const handleExportJSON = () => {
    if (!selectedDbId || !selectedElementName || properties.length === 0) return;
    PropertiesService.exportToJSON(selectedElementName, selectedDbId, properties);
  };

  if (!isModelLoaded) {
    return (
      <div className="animate-fade-in p-4">
        <div className="text-center text-white">
          <p>Please load a model first to inspect properties</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-100">
        Properties Inspector
      </h2>

      {/* Selection Mode Toggle */}
      <div className="mb-4 p-3 rounded-lg bg-dark-200 border border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={isSelectionMode}
                onChange={handleToggleSelectionMode}
              />
              <span className="text-sm font-medium text-gray-100">
                Enable Click Selection Mode
              </span>
            </label>
            <p className="text-xs text-white mt-1 ml-6">
              {isSelectionMode 
                ? 'Click on any element in the viewer to inspect its properties'
                : 'Enable to select elements directly from the viewer'}
            </p>
          </div>
          {selectedDbId && (
            <button
              className="btn btn-secondary btn-small"
              onClick={handleClearSelection}
            >
              Clear Selection
            </button>
          )}
        </div>
      </div>

      {/* Direct Element ID Input */}
      <div className="mb-4 p-3 rounded-lg bg-dark-200 border border-gray-700">
        {/* ID Type Toggle */}
        <div className="flex gap-2 mb-3">
          <button
            className={`btn btn-small flex-1 ${idType === 'dbId' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setIdType('dbId')}
          >
            Viewer ID (DbID)
          </button>
          <button
            className={`btn btn-small flex-1 ${idType === 'revitId' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setIdType('revitId')}
          >
            Revit Element ID
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-white mb-1">
              Enter {idType === 'dbId' ? 'Viewer ID (DbID)' : 'Revit Element ID'}
            </label>
            <input
              type={idType === 'dbId' ? 'number' : 'text'}
              className="input-dark w-full"
              placeholder={idType === 'dbId' ? 'e.g., 1234' : 'e.g., 305890'}
              value={inputDbId}
              onChange={(e) => setInputDbId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleLoadElement();
                }
              }}
            />
          </div>
          <button
            className="btn btn-primary mt-6"
            onClick={handleLoadElement}
            disabled={!inputDbId || isLoading}
          >
            {isLoading ? '⏳ Loading...' : 'Load Properties'}
          </button>
        </div>
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-white">
            Enter a specific element ID to inspect its properties directly
          </p>
          <button
            className="text-xs text-primary hover:text-primary-light underline"
            onClick={async () => {
              if (viewer && viewer.model) {
                if (idType === 'dbId') {
                  // Get sample DbIDs
                  const tree = viewer.model.getInstanceTree();
                  if (tree) {
                    const sampleIds: number[] = [];
                    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
                      if (sampleIds.length < 5) {
                        sampleIds.push(dbId);
                      }
                    }, true);
                    if (sampleIds.length > 0) {
                      alert(`Sample Viewer IDs (DbIDs) from this model:\n${sampleIds.join(', ')}\n\nTry entering one of these IDs.`);
                    }
                  }
                } else {
                  // Get sample Revit IDs
                  const tree = viewer.model.getInstanceTree();
                  if (tree) {
                    const sampleDbIds: number[] = [];
                    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
                      if (sampleDbIds.length < 5) {
                        sampleDbIds.push(dbId);
                      }
                    }, true);
                    
                    // Get Revit IDs for these elements
                    const revitIds: string[] = [];
                    for (const dbId of sampleDbIds) {
                      await new Promise<void>((resolve) => {
                        viewer.getProperties(dbId, (result) => {
                          if (result) {
                            const idProp = result.properties.find(p => 
                              p.displayName === 'Element ID' || 
                              p.displayName === 'Id' ||
                              p.displayName === 'ElementId'
                            );
                            if (idProp && idProp.displayValue) {
                              revitIds.push(String(idProp.displayValue));
                            }
                          }
                          resolve();
                        }, () => resolve());
                      });
                    }
                    
                    if (revitIds.length > 0) {
                      alert(`Sample Revit Element IDs from this model:\n${revitIds.join(', ')}\n\nTry entering one of these IDs.`);
                    } else {
                      alert('Could not find Revit Element IDs in this model. The model may not contain Revit ID properties.');
                    }
                  }
                }
              }
            }}
          >
            Get sample {idType === 'dbId' ? 'DbIDs' : 'Revit IDs'}
          </button>
        </div>
      </div>

      {/* Property Search */}
      <PropertySearch />

      {/* Status Bar */}
      {(error || isLoading || selectedDbId) && (
        <div className={`mb-4 p-3 rounded-lg border ${
          error 
            ? 'bg-red-900/20 border-red-800' 
            : isLoading
            ? 'bg-yellow-900/20 border-yellow-800'
            : 'bg-green-900/20 border-green-800'
        }`}>
          <div className="flex items-center justify-between">
            <span className="text-sm">
              {error ? (
                <span className="text-red-400">Error: {error}</span>
              ) : isLoading ? (
                <span className="text-yellow-400">⏳ Loading properties...</span>
              ) : selectedDbId ? (
                <span className="text-green-400">
                  ✅ Inspecting: {selectedElementName} (ID: {selectedDbId})
                </span>
              ) : null}
            </span>
          </div>
        </div>
      )}

      {/* Selected Element Info */}
      {selectedDbId && !isLoading && (
        <>
          <div className="mb-4 p-3 rounded-lg bg-dark-200 border border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-white">{selectedElementName}</h3>
                <p className="text-xs text-white">
                  DbID: {selectedDbId} | Properties: {properties.length}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-secondary btn-small"
                  onClick={() => loadElementProperties(selectedDbId)}
                >
                  🔄 Refresh
                </button>
                <button
                  className="btn btn-success btn-small"
                  onClick={handleExportCSV}
                >
                  📊 CSV
                </button>
                <button
                  className="btn btn-success btn-small"
                  onClick={handleExportJSON}
                >
                  📄 JSON
                </button>
              </div>
            </div>
          </div>

          {/* Show Hidden Properties Toggle */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={showHiddenProperties}
                onChange={toggleShowHidden}
              />
              <span className="text-sm text-gray-100">Show hidden properties</span>
            </label>
          </div>

          {/* Properties List */}
          <PropertyList />

          {/* Geometry Tools Testing Section */}
          <div className="mt-6">
            <div className="mb-4 p-3 rounded-lg bg-dark-200 border border-gray-700">
              <button
                className="flex items-center justify-between w-full text-left"
                onClick={() => setShowGeometryTester(!showGeometryTester)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🧪</span>
                  <h3 className="font-medium text-white">Geometry Tools Testing</h3>
                </div>
                <span className="text-white">
                  {showGeometryTester ? '▼' : '▶'}
                </span>
              </button>
              <p className="text-xs text-white mt-1 ml-7">
                Test geometry analysis methods and visualize results in the viewer
              </p>
            </div>
            
            {showGeometryTester && (
              <div className="animate-fade-in">
                <GeometryToolsTester elementDbId={selectedDbId} />
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State */}
      {!selectedDbId && !isLoading && !error && (
        <div className="text-center py-8 text-white">
          <p className="mb-2">No element selected</p>
          <p className="text-xs">
            {isSelectionMode 
              ? 'Click on an element in the viewer to inspect it'
              : 'Enable selection mode to pick an element'}
          </p>
        </div>
      )}
    </div>
  );
}